import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  UsageDetailsData,
  ApiKeyUsageDetail,
} from '../../../utils/openai-api';
import { getDevSettings } from '../../../utils/storage';
import { LabelDefinition } from '@/types/label-budget';
import { EmployeeRecord } from '@/utils/employee-indexeddb';
import { DateRange } from '../../../constants/ui';
import { SortState } from '../types';
import { matchesOrganizationFilter } from '../utils/organization';
import { FilteredDataResult, getApiKeyUniqueId } from './useFilteredData';
import type { ApiKeyMetadata } from '../../../types/label-budget';
import { debugLog } from '../../../utils/debug-log';

export interface UseSortedPaginatedDataParams {
  data: UsageDetailsData | null;
  filteredData: FilteredDataResult | null;
  filter: string;
  sort: SortState;
  setSort: React.Dispatch<React.SetStateAction<SortState>>;
  selectedWorkspace: string;
  selectedModel: string;
  apiKeyMetadata: Record<string, Partial<ApiKeyMetadata>>;
  labelDefinitions: LabelDefinition[];
  selectedDepartment: string;
  normalizedDepartmentFilter: string;
  selectedRole: string;
  employeeNameFilter: string;
  employeeData: Map<string, EmployeeRecord>;
  updateURL: (filter: string, sort: SortState, dateRange: DateRange, customDateRange: { startDate: Date; endDate: Date } | null, workspace: string, model: string) => void;
  dateRange: DateRange;
  customDateRange: { startDate: Date; endDate: Date } | null;
}

export interface UseSortedPaginatedDataReturn {
  filteredSortedApiKeys: ApiKeyUsageDetail[];
  paginatedApiKeys: ApiKeyUsageDetail[];
  currentOffset: number;
  setCurrentOffset: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  handleSort: (key: string) => void;
}

export function useSortedPaginatedData({
  data,
  filteredData,
  filter,
  sort,
  setSort,
  selectedWorkspace,
  selectedModel,
  apiKeyMetadata,
  labelDefinitions,
  selectedDepartment,
  normalizedDepartmentFilter,
  selectedRole,
  employeeNameFilter,
  employeeData,
  updateURL,
  dateRange,
  customDateRange,
}: UseSortedPaginatedDataParams): UseSortedPaginatedDataReturn {
  const [currentOffset, setCurrentOffset] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    const devSettings = getDevSettings();
    const limit = devSettings.apiKeyLimit;
    return limit === 'unlimited' ? 0 : (limit || 100);
  });

  useEffect(() => {
    const devSettings = getDevSettings();
    const limit = devSettings.apiKeyLimit;
    setPageSize(limit === 'unlimited' ? 0 : (limit || 100));
  }, []);

  // Listen for dev settings changes to update page size
  useEffect(() => {
    const handleSettingsChange = () => {
      const devSettings = getDevSettings();
      const limit = devSettings.apiKeyLimit;
      setPageSize(limit === 'unlimited' ? 0 : (limit || 100));
    };

    window.addEventListener('devSettingsChanged', handleSettingsChange);
    return () => window.removeEventListener('devSettingsChanged', handleSettingsChange);
  }, []);

  // Reset offset when sort, filter, workspace, or employee filters change
  useEffect(() => {
    setCurrentOffset(0);
  }, [sort.key, sort.dir, filter, selectedWorkspace, selectedModel, selectedDepartment, selectedRole, employeeNameFilter]);

  // Client-side filtering, sorting, and pagination
  const filteredSortedApiKeys = useMemo(() => {
    if (!filteredData) return [];

    const getUnusedEmployees = () => {
      const usedEmails = new Set<string>();
      const usedApiKeyIds = new Set<string>();

      if (data?.apiKeyUsage) {
        data.apiKeyUsage.forEach(apiKey => {
          usedApiKeyIds.add(getApiKeyUniqueId(apiKey));
          usedApiKeyIds.add(apiKey.id);
          const apiKeyEmail = apiKey.name.toLowerCase();
          usedEmails.add(apiKeyEmail);
          if (apiKeyEmail.includes('@')) {
            usedEmails.add(apiKeyEmail.split('@')[0]);
          }
          if (apiKey.ownerEmail) {
            usedEmails.add(apiKey.ownerEmail.toLowerCase());
            const ownerPrefix = apiKey.ownerEmail.toLowerCase().split('@')[0];
            usedEmails.add(ownerPrefix);
          }
        });
      }

      const unusedEmployees: ApiKeyUsageDetail[] = [];
      const uniqueEmps = new Set<EmployeeRecord>();
      employeeData.forEach(emp => uniqueEmps.add(emp));

      Array.from(uniqueEmps).forEach(employee => {
        const emailLower = employee.email.toLowerCase();
        const emailPrefix = emailLower.split('@')[0];

        const hasUsage = usedEmails.has(emailLower) ||
                        usedEmails.has(emailPrefix) ||
                        (employee.apiKeyId && (
                          usedApiKeyIds.has(employee.apiKeyId) ||
                          usedApiKeyIds.has(`openai:${employee.apiKeyId}`) ||
                          usedApiKeyIds.has(`anthropic:${employee.apiKeyId}`) ||
                          usedApiKeyIds.has(`unknown:${employee.apiKeyId}`)
                        ));

        if (!hasUsage) {
          if (!matchesOrganizationFilter(employee, normalizedDepartmentFilter)) {
            return;
          }

          if (selectedRole !== '') {
            if (!employee.role) return;
            try {
              const regex = new RegExp(selectedRole, 'i');
              if (!regex.test(employee.role)) return;
            } catch  {
              if (!employee.role.toLowerCase().includes(selectedRole.toLowerCase())) return;
            }
          }

          if (employeeNameFilter !== '') {
            if (!employee.name) return;
            try {
              const regex = new RegExp(employeeNameFilter, 'i');
              if (!regex.test(employee.name)) return;
            } catch  {
              if (!employee.name.toLowerCase().includes(employeeNameFilter.toLowerCase())) return;
            }
          }

          unusedEmployees.push({
            id: `no-usage-${employee.email}`,
            name: employee.email,
            requests: 0,
            days: 0,
            firstSeen: '-',
            lastSeen: '-',
            contextTokens: 0,
            generatedTokens: 0,
            cachedTokens: 0,
            cost: 0,
            cachedCost: 0,
            cachedPercentage: 0,
            costPer100Requests: 0,
            status: 'Inactive' as const,
            modelCosts: {},
            modelUsage: {},
            projectId: '',
            projectName: '',
            numberOfRequests: 0,
            totalCost: 0
          } as ApiKeyUsageDetail);
        }
      });

      return unusedEmployees;
    };

    if (normalizedDepartmentFilter === 'no-usage') {
      const unusedEmployees = getUnusedEmployees();
      if (import.meta.env.DEV) debugLog('Showing only unused employees:', unusedEmployees.length);
      return unusedEmployees;
    }

    const filtered = [...filteredData.apiKeyUsage];

    let combinedResults: typeof filtered = filtered;
    if (normalizedDepartmentFilter !== 'all' &&
        normalizedDepartmentFilter !== 'no-mapping' &&
        normalizedDepartmentFilter !== 'no-usage') {
      const unusedEmployees = getUnusedEmployees();
      if (import.meta.env.DEV) debugLog(`Adding ${unusedEmployees.length} unused employees to ${filtered.length} active users`);
      combinedResults = [...filtered, ...unusedEmployees];
    }

    if (sort.key) {
      combinedResults = [...combinedResults].sort((a, b) => {
        let aVal: unknown;
        let bVal: unknown;

        if (sort.key!.startsWith('label_')) {
          const labelId = sort.key!.replace('label_', '');
          const getMetadataFor = (entry: ApiKeyUsageDetail) => {
            const metadataKey = entry.sourceIds?.find(id => apiKeyMetadata[id]) ?? entry.id;
            return apiKeyMetadata[metadataKey] || {};
          };
          const aMetadata = getMetadataFor(a);
          const bMetadata = getMetadataFor(b);
          aVal = aMetadata.labels?.[labelId] || '';
          bVal = bMetadata.labels?.[labelId] || '';

          const labelDef = labelDefinitions.find(def => def.id === labelId);
          if (labelDef) {
            if (labelDef.type === 'number' || labelDef.type === 'currency') {
              aVal = aVal === '' ? 0 : parseFloat(String(aVal));
              bVal = bVal === '' ? 0 : parseFloat(String(bVal));
              if (Number.isNaN(aVal)) aVal = 0;
              if (Number.isNaN(bVal)) bVal = 0;
            }
          }
        } else {
          aVal = a[sort.key as keyof ApiKeyUsageDetail];
          bVal = b[sort.key as keyof ApiKeyUsageDetail];
        }

        if (sort.key === 'requests' || sort.key === 'cost' || sort.key === 'contextTokens' ||
            sort.key === 'generatedTokens' || sort.key === 'cachedTokens' ||
            sort.key === 'costPer100Requests' || sort.key === 'cachedCost' ||
            sort.key === 'cachedPercentage' || sort.key === 'days') {
          if (aVal === undefined || aVal === null) aVal = 0;
          if (bVal === undefined || bVal === null) bVal = 0;
        }

        if (sort.key === 'firstSeen' || sort.key === 'lastSeen') {
          if ((aVal === 'N/A' || aVal === '-') && (bVal !== 'N/A' && bVal !== '-')) {
            return sort.dir === 'asc' ? -1 : 1;
          }
          if ((aVal !== 'N/A' && aVal !== '-') && (bVal === 'N/A' || bVal === '-')) {
            return sort.dir === 'asc' ? 1 : -1;
          }
          if ((aVal === 'N/A' || aVal === '-') && (bVal === 'N/A' || bVal === '-')) {
            return 0;
          }
        }

        if (aVal < bVal) return sort.dir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sort.dir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return combinedResults;
  }, [filteredData, sort, apiKeyMetadata, labelDefinitions, selectedRole, employeeNameFilter, employeeData, data, normalizedDepartmentFilter]);

  const paginatedApiKeys = useMemo(() => {
    if (pageSize === 0) {
      return filteredSortedApiKeys;
    }
    const startIndex = currentOffset;
    const endIndex = startIndex + pageSize;
    if (import.meta.env.DEV) debugLog('Pagination debug:', {
      totalItems: filteredSortedApiKeys.length,
      pageSize,
      currentOffset,
      startIndex,
      endIndex,
      resultLength: filteredSortedApiKeys.slice(startIndex, endIndex).length
    });
    return filteredSortedApiKeys.slice(startIndex, endIndex);
  }, [filteredSortedApiKeys, currentOffset, pageSize]);

  const handleSort = useCallback((key: string) => {
    const newDir = (sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc') as 'asc' | 'desc';

    const newSort = { key, dir: newDir };
    setSort(newSort);
    updateURL(filter, newSort, dateRange, customDateRange, selectedWorkspace, selectedModel);
  }, [sort, filter, updateURL, dateRange, customDateRange, selectedWorkspace, selectedModel, setSort]);

  return {
    filteredSortedApiKeys,
    paginatedApiKeys,
    currentOffset,
    setCurrentOffset,
    pageSize,
    setPageSize,
    handleSort,
  };
}
